import { noop } from 'lodash';
import { PropsWithChildren, createContext, useContext } from 'react';
import { NoOpComponent } from 'utils/reactHelpers';
import { DefaultModalProps, ModalContextState, useModals } from '../../hooks/useModals';

export type ModalComponent<Props> = (props: Props) => JSX.Element | null;
export type ModalsMapper<ModalType extends string, ModalProps> = Record<
  ModalType,
  ModalComponent<ModalProps>
>;

export const DEFAULT_MODAL_CONTEXT_STATE = {
  openedModals: [],
  openModal: noop,
  closeModal: noop,
  closeAllModals: noop,
};

/**
 * Hi there! This abstract modal opened/closed state managements via context
 * @todo Make `ModalProps` more strict
 * @param providerName - Component name for the Provider. This name is used by React in debugging messages (https://stackoverflow.com/a/41582557)
 * @param defaultContextState - default state for the context
 * @param useHandleModals - hook for handling the modal logics
 *
 * @example
 *
 * 1. Create your modal context
 * enum ModalType {
 *  ConfirmDeleteUser = 'ConfirmDeleteUser',
 *  UnsavedDeliverySettings = 'UnsavedDeliverySettings'
 * }
 *
 * type ModalProps = ConfirmDeleteUserModalProps | UnsavedDeliverySettingsModalProps;
 *
 * export const { ModalContext, ModalProvider, useModalContext } = createModalContext<ModalType, ModalProps>();
 *
 * 2. Setup your root component
 *
 * const CampaignEditor = () => (
 * <OtherProvider>
 *   <ModalProvider>
 *      <Modals/>
 *      <OtherConsumingComponent/>
 *    </ModalProvider>
 * </OtherProvider>
 * );
 *
 * 3. Access the modal context in consuming components
 * const ConfirmDeleteUserModal = () => {
 *   const { closeModal } = useModalContext();
 *   return (
 *     <Modal onClose={() => closeModal(ModalType.ConfirmDeleteUser)}>
 *       I am a real fake modal
 *     </Modal>
 *   );
 * };
 *
 * const OtherConsumingComponent = () => {
 *   const { openModal } = useModalContext();
 *   return (
 *     <div>
 *       <button onClick={() => openModal(ModalType.ConfirmDeleteUser)}>
 *         Open ConfirmDeleteUser!
 *       </button>
 *     </div>
 *   );
 * };
 *
 * const MODAL_COMPONENTS_MAPPER: ModalsMapper<ModalType, any> = {
 *  [ModalType.ConfirmDeleteUser]: ConfirmDeleteUserModal,
 *  [ModalType.UnsavedDeliverySettings]: UnsavedDeliverySettingsModal,
 * };
 *
 * export const Modals = createModalsComponent<ModalType, ModalProps>(
 *  MODAL_COMPONENTS_MAPPER,
 *  useModalContext,
 * );
 *
 */
export const createModalContext = <
  ModalType extends string,
  ModalProps extends DefaultModalProps = DefaultModalProps,
>(
  providerName = 'ModalProvider',
  defaultContextState = DEFAULT_MODAL_CONTEXT_STATE as ModalContextState<ModalType, ModalProps>,
  useHandleModalsState = useModals,
) => {
  const ModalContext = createContext<ModalContextState<ModalType, ModalProps>>(defaultContextState);

  const useModalContext = () => useContext(ModalContext);

  const ModalProvider = ({ children }: PropsWithChildren<unknown>) => {
    const value = useHandleModalsState<ModalType, ModalProps>();
    return <ModalContext.Provider value={value}>{children}</ModalContext.Provider>;
  };

  ModalProvider.displayName = providerName;

  return {
    ModalContext,
    ModalProvider,
    useModalContext,
  } as const;
};

/**
 * Creates a component that renders the currently opened modals
 * @param mapper - A dictionary of modal components to be rendered when toggled on
 * @param useModalsState - A hook that provides the current opened modals state
 *
 * @example
 * const MODAL_COMPONENTS_MAPPER: ModalsMapper<ModalType, any> = {
 *  [ModalType.SelectContentEditorModal]: SelectContentEditorModal,
 * };
 *
 * export const Modals = createModalsComponent<ModalType, ModalProps>(
 *  MODAL_COMPONENTS_MAPPER,
 *  useModalContext,
 * );
 */
export const createModalsComponent =
  <ModalType extends string, ModalProps extends DefaultModalProps = DefaultModalProps>(
    mapper: ModalsMapper<ModalType, ModalProps>,
    useModalContext: () => ModalContextState<ModalType, ModalProps>,
  ) =>
  () => {
    const { openedModals } = useModalContext();

    return (
      <>
        {openedModals.map(({ type, props = {} as ModalProps }) => {
          /** Defaults to `NoOpComponent` if value is not available */
          const Modal = (mapper?.[type] || NoOpComponent) as ModalComponent<ModalProps>;
          return <Modal key={type} {...props} />;
        })}
      </>
    );
  };
